import { useState, useEffect, FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Input } from '../../common/components/inputs';
import { Button } from '../../common/components/buttons';
import { SuccessMessage } from '../../common/components/feedback';
import { AuthCard, AuthLayout } from '../ui';
import { verifyEmail, resendVerificationEmail } from '../../../services/authService';
import { RESEND_COOLDOWN_SECONDS, REDIRECT_COUNTDOWN_SECONDS } from '../constants';

/**
 * VerifyEmailPage - Página de verificação de email
 */
export const VerifyEmailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract email from various sources
  const urlParams = new URLSearchParams(window.location.search);
  const emailFromQuery = urlParams.get('email');
  const email = location.state?.email || localStorage.getItem('pendingVerificationEmail') || emailFromQuery;

  // Form state
  const [verificationCode, setVerificationCode] = useState('');

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendSuccess, setResendSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(0);

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldown]);

  // Redirect timer after success
  useEffect(() => {
    if (redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (redirectCountdown === 0 && success) {
      // Use a small timeout to avoid race condition with navigate
      const redirectTimer = setTimeout(() => {
        navigate('/');
      }, 100);
      return () => clearTimeout(redirectTimer);
    }
  }, [redirectCountdown, navigate, success]);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setResendSuccess('');
    setSuccess('');

    if (!email) {
      setError('Email não encontrado. Por favor, faça o registro novamente.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await verifyEmail(email, verificationCode);
      setSuccess(response.message || 'Email verificado com sucesso!');
      localStorage.removeItem('pendingVerificationEmail');
      setRedirectCountdown(REDIRECT_COUNTDOWN_SECONDS);
    } catch (err: unknown) {
      // Log error for debugging without console.error in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Verification error:', err);
      }
      setError(err instanceof Error ? err.message : 'Erro ao verificar email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || isResending || cooldown > 0) return;

    try {
      setIsResending(true);
      setError('');
      setResendSuccess('');
      setSuccess('');

      const response = await resendVerificationEmail(email);
      setResendSuccess(response.message || 'Novo código de verificação enviado! Verifique seu email.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      // Log error for debugging without console.error in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Resend error:', err);
      }
      setError(err instanceof Error ? err.message : 'Erro ao reenviar código de verificação');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard title="Verificar Email" subtitle={`Um código de verificação foi enviado para:`} showTabs={false}>
        <form onSubmit={handleVerify} className="space-y-5">
          {/* Email Display */}
          <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-4 text-center">
            <div className="text-white font-semibold text-[15px]">{email}</div>
          </div>

          {/* Verification Code Input */}
          <Input
            label="Código de verificação"
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            error={error}
            icon={Mail}
            maxLength={6}
            required
          />

          {/* Success Message */}
          {success && (
            <SuccessMessage
              additionalInfo={
                redirectCountdown > 0 ? (
                  <>Redirecionando em {redirectCountdown} segundos...</>
                ) : undefined
              }
            >
              {success}
            </SuccessMessage>
          )}

          {/* Resend Success Message */}
          {resendSuccess && <SuccessMessage>{resendSuccess}</SuccessMessage>}

          {/* Verify Button */}
          <Button type="submit" variant="primary" fullWidth loading={isLoading} disabled={success !== ''}>
            Verificar Email
          </Button>

          {/* Resend Code */}
          <div className="text-center text-[#CCCCCC] text-sm">
            Não recebeu o código?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending || cooldown > 0}
              className="text-[#F4CC04] hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending
                ? 'Reenviando...'
                : cooldown > 0
                ? `Reenviar código (${cooldown}s)`
                : 'Reenviar código'}
            </button>
          </div>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};
